import { FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShopLayout } from "@/components/ShopLayout";
import { api, ApiError } from "@/lib/api";
import { saveShopSession, type ShopSession } from "@/lib/shopAuth";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";

type EmailPhase = "email" | "code";

function resolveReturnTo(state: unknown): string {
  if (
    state &&
    typeof state === "object" &&
    "returnTo" in state &&
    typeof (state as { returnTo: unknown }).returnTo === "string"
  ) {
    const path = (state as { returnTo: string }).returnTo;
    if (path.startsWith("/") && !path.startsWith("//")) {
      return path;
    }
  }
  return "/";
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = resolveReturnTo(location.state);
  const { siteName } = useCampsiteBranding();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [emailPhase, setEmailPhase] = useState<EmailPhase>("email");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function requestCode(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.post<{ message: string }>(
        "/api/v1/public/shop/auth/request-code",
        { email: email.trim() },
        { auth: false },
      );
      setMessage(res.message);
      setEmailPhase("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Code konnte nicht angefordert werden.");
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const session = await api.post<ShopSession>(
        "/api/v1/public/shop/auth/verify",
        { email: email.trim(), code: code.trim() },
        { auth: false },
      );
      saveShopSession(session);
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anmeldung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <ShopLayout>
      <div className="shop-page flex justify-center">
        <div className="shop-card w-full max-w-md animate-slide-up">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Willkommen</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Melden Sie sich mit Ihrer E-Mail-Adresse an, um am Platz Artikel vorzubestellen – z. B.
              Brötchen für den nächsten Morgen.
            </p>
          </div>

          {emailPhase === "email" ? (
            <form onSubmit={(e) => void requestCode(e)} className="space-y-4">
              <label className="block text-sm">
                <span className="mb-1.5 block font-medium text-slate-700">E-Mail-Adresse</span>
                <input
                  type="email"
                  className="shop-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ihre bei der Buchung hinterlegte E-Mail"
                  required
                  autoComplete="email"
                />
              </label>
              <p className="text-xs text-slate-500">
                Nur für eingecheckte Gäste mit hinterlegter E-Mail-Adresse am {siteName}.
              </p>
              {error ? <p className="text-sm text-red-600">{error}</p> : null}
              <button type="submit" disabled={loading} className="shop-btn-primary w-full">
                {loading ? "Code wird gesendet…" : "Code anfordern"}
              </button>
            </form>
          ) : (
            <form onSubmit={(e) => void verifyCode(e)} className="space-y-4">
              {message ? (
                <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                  {message}
                </div>
              ) : null}
              <label className="block text-sm">
                <span className="mb-1.5 block font-medium text-slate-700">Bestätigungscode</span>
                <input
                  className="shop-input text-center tracking-[0.3em]"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
              </label>
              {error ? <p className="text-sm text-red-600">{error}</p> : null}
              <button type="submit" disabled={loading} className="shop-btn-primary w-full">
                {loading ? "Wird geprüft…" : "Anmelden"}
              </button>
              <button
                type="button"
                className="shop-btn-ghost w-full"
                onClick={() => {
                  setEmailPhase("email");
                  setCode("");
                  setError(null);
                }}
              >
                Andere E-Mail verwenden
              </button>
            </form>
          )}
        </div>
      </div>
    </ShopLayout>
  );
}
